import { createRouter, createWebHashHistory } from "vue-router";
import Home from "../views/Home";
import store from "../store";
import {
  isSessionInvalidated,
  purgeSession,
  validateSession,
} from "../utils/sessionGuard";

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/about",
    name: "About",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/About"),
  },
  {
    path: "/login",
    name: "Login",
    meta: {
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "login" */ "../views/Login"),
  },
  {
    path: "/signup",
    name: "SignUp",
    meta: {
      requiresAuth: false,
    },
    component: () => import(/* webpackChunkName: "signup" */ "../views/SignUp"),
  },
  {
    path: "/feed",
    name: "feed",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "feed" */ "../views/Feed"),
  },
  {
    path: "/search",
    name: "search",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "search" */ "../views/Search"),
  },
  {
    path: "/reply/:id/:from",
    name: "reply",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "reply" */ "../views/Reply"),
  },
  {
    path: "/post/:from",
    name: "post",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "post" */ "../views/NewPost"),
  },
  {
    path: "/profile",
    name: "profile",
    meta: {
      requiresAuth: true,
    },
    component: () =>
      import(/* webpackChunkName: "profile" */ "../views/Profile"),
  },
  {
    path: "/collab/:sessionId",
    name: "collab",
    meta: {
      requiresAuth: true,
    },
    component: () =>
      import(/* webpackChunkName: "collab" */ "../views/Collab"),
  },
  {
    path: "/u/:username",
    name: "publicProfile",
    meta: {
      requiresAuth: true,
    },
    component: () =>
      import(/* webpackChunkName: "publicprofile" */ "../views/PublicProfile"),
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// Recover from chunk-load failures that strand the user on a blank page.
// After a service-worker update + reload, the new `index.html` references new
// hashed chunk filenames; if the matching chunk is missing from the precache
// (e.g. a half-applied SW update on iOS Safari, or a network blip mid-reload),
// the dynamic `import(...)` for the route component rejects. Vue Router
// silently swallows that rejection — `<router-view>` renders nothing and the
// body's lavender gradient is all that remains, which is exactly the
// "blank Feed after Update modal" symptom reported on iOS.
//
// Strategy: on the first chunk-load failure per tab, force-reload once with
// a cache-busting query string. A sessionStorage flag guards against an
// infinite reload loop if the chunk is genuinely permanently missing.
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [^ ]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

function isChunkLoadError(err) {
  if (!err) return false;
  const msg = (err && (err.message || err.toString())) || "";
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg));
}

router.onError((err) => {
  if (!isChunkLoadError(err)) return;
  try {
    if (sessionStorage.getItem("_scrawl_chunk_reload") === "1") return;
    sessionStorage.setItem("_scrawl_chunk_reload", "1");
  } catch {
    // Storage blocked (private mode / ITP) — fall through and reload anyway.
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now());
  window.location.replace(url.toString());
});

// Clear the reload-guard once the app has successfully navigated to a real
// route after a recovery reload, so a *future* SW update can recover again.
router.afterEach(() => {
  try {
    if (sessionStorage.getItem("_scrawl_chunk_reload") === "1") {
      sessionStorage.removeItem("_scrawl_chunk_reload");
    }
  } catch {
    // ignore
  }
});

router.beforeEach(async (to, from, next) => {
  await validateSession();
  if (isSessionInvalidated()) {
    await purgeSession();
    return to.path === "/" ? next() : next("/");
  }

  let isAuth = store.getters["auth/isAuthenticated"];

  if (
    to.matched.some((record) => {
      if (record.meta.requiresAuth) {
        //Route needs auth
        //Check if the user is logged in
        if (isAuth) {
          next();
        } else {
          //Send user to home page
          next("/");
        }
      } else {
        if (isAuth) {
          //User is logged in but trying to access non secure page
          next("/feed");
        } else {
          next();
        }
      }
    })
  );
});

export default router;
